import { FC, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  BatteryDark,
  ManDark,
  MechanicDark,
  PaintGunDark,
  PeopleDark,
  PhoneDark,
  VanDark,
  WheelDark,
  WomanDark,
  WrenchDark,
  BatteryLight,
  ManLight,
  MechanicLight,
  PaintGunLight,
  PeopleLight,
  PhoneLight,
  VanLight,
  WheelLight,
  WomanLight,
  WrenchLight,
  LogoIconBlank,
} from '@carcareers/assets';

export interface IconSpinnerProps {
  size?: number;
  iconSize?: number;
  radius?: number;
  rotationDuration?: number;
}

export const IconSpinner: FC<IconSpinnerProps> = ({
  size = 500,
  iconSize = 50,
  radius = 150,
  rotationDuration = 60,
}) => {
  const theme = useTheme();

  const icons = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return isDark
      ? [
          BatteryDark,
          ManDark,
          MechanicDark,
          PaintGunDark,
          PeopleDark,
          PhoneDark,
          VanDark,
          WheelDark,
          WomanDark,
          WrenchDark,
        ]
      : [
          BatteryLight,
          ManLight,
          MechanicLight,
          PaintGunLight,
          PeopleLight,
          PhoneLight,
          VanLight,
          WheelLight,
          WomanLight,
          WrenchLight,
        ];
  }, [theme.palette.mode]);

  const numIcons = icons.length;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
      }}
    >
      {/* Center Logo */}
      <Box
        sx={{
          position: 'absolute',
          width: iconSize * 1.5,
          height: iconSize * 1.75,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          '& svg': {
            width: '100%',
            height: '100%',
            display: 'block',
            overflow: 'visible',
          },
        }}
      >
        <LogoIconBlank />
      </Box>

      {/* Rotating Container */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          animation: {
            xs: 'none',
            md: `spin ${rotationDuration}s linear infinite`,
          },
          '@keyframes spin': {
            '0%': {
              transform: 'rotate(0deg)',
            },
            '100%': {
              transform: 'rotate(360deg)',
            },
          },
        }}
      >
        {icons.map((Icon, index) => {
          const angle = (index * 360) / numIcons;
          const angleRad = (angle * Math.PI) / 180;
          const x = Math.cos(angleRad) * radius;
          const y = Math.sin(angleRad) * radius;

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                left: `calc(50% + ${x}px - ${iconSize / 2}px)`,
                top: `calc(50% + ${y}px - ${iconSize / 2}px)`,
                width: iconSize,
                height: iconSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: {
                  xs: 'none',
                  md: `counterSpin ${rotationDuration}s linear infinite`,
                },
                '@keyframes counterSpin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(-360deg)' },
                },
                '& svg': {
                  width: '100%',
                  height: '100%',
                  display: 'block',
                },
              }}
            >
              <Icon />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default IconSpinner;
